/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProjectEmployeeCost.scss';
import { TableUtility } from '@utility/dynamic-table';
import MilestoneInitial from '../MilestoneMIS/MilestoneInitial';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Toast } from 'primereact/toast';

import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { PROJECT_EMPLOYEE_COST } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.RESELLER_SERVICE);
  const servicePort = appendServicePortToURL(serviceName.RESELLER_SERVICE);
  let baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  // If no REACT_APP_API_URL is provided, fallback to window.origin (if available)
  // or default to http://localhost to ensure the URL has a valid scheme.
  if (!baseUrl) {
    // In development, prefer explicit localhost so we can append the known service port.
    if (
      process.env.NODE_ENV === 'production'
      && typeof window !== 'undefined'
      && window.location
      && window.location.origin
    ) {
      baseUrl = window.location.origin.replace(/\/$/, '');
    } else {
      baseUrl = 'http://localhost';
    }
  }

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${PROJECT_EMPLOYEE_COST}`;
  }

  // If baseUrl already contains a port, avoid appending another port.
  const hasPort = /:\d+$/.test(baseUrl);
  if (hasPort) {
    return `${baseUrl}${serviceURL}/api${PROJECT_EMPLOYEE_COST}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${PROJECT_EMPLOYEE_COST}`;
};

const baseURL = fetchBaseURL();

const isMonthFilter = (item) => {
  const template = (item?.template || '').toString().toLowerCase();
  const type = (item?.type || '').toString().toLowerCase();
  const pickerType = (item?.pickerType || '').toString().toLowerCase();
  const displayFormat = (item?.displayFormat || '').toString().toLowerCase();

  return (
    template === 'monthcalendar'
    || template === 'month'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm'
  );
};

const toMonthCalendarValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = value.toString().trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMonthYearPart = (yearPart) => {
  const normalizedYear = String(yearPart || '').trim();
  if (!normalizedYear) return null;
  if (normalizedYear.length === 2) return `20${normalizedYear}`;
  return /^\d{4}$/.test(normalizedYear) ? normalizedYear : null;
};

const toApiMonthRangeValue = (value, { useEndOfMonth = false } = {}) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = useEndOfMonth ? new Date(year, month, 0).getDate() : 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (numericMonthYearMatch) {
    const [, monthPart, yearPart] = numericMonthYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    const month = Number(monthPart);
    if (!year || Number.isNaN(month) || month < 1 || month > 12) return null;
    const lastDay = new Date(Number(year), month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const monthNameYearMatch = normalized.match(/^([A-Za-z]{3,9})[-/\s](\d{2}|\d{4})$/);
  if (monthNameYearMatch) {
    const [, monthName, yearPart] = monthNameYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    if (!year) return null;
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return toApiMonthRangeValue(parsed, { useEndOfMonth });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return toApiMonthRangeValue(parsedDate, { useEndOfMonth });
};

const extractMonthRangePayload = (container) => {
  const inputs = Array.from(container?.querySelectorAll('input') || []);
  const [fromInput, toInput] = inputs;

  return {
    fromMonthYear: toApiMonthRangeValue(fromInput?.value || null),
    toMonthYear: toApiMonthRangeValue(toInput?.value || null, { useEndOfMonth: true }),
  };
};

const normalizeMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          const normalizedTemplate = (item?.template || '').toString().toLowerCase();
          // Keep explicit dateRange filters intact (they render the 2-input
          // range component directly after selecting the filter option).
          if (normalizedTemplate === 'daterange') return item;
          if (!isMonthFilter(item)) return item;

          // Match Project Utilization: month template gets routed through
          // monthCalendar so dynamic-table renders YearMonthCalendarFilter.
          return {
            ...item,
            template: 'monthCalendar',
            selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
          };
        })
        : menuItem?.items,
    }))
    : []
);

const NUMERIC_COLUMN_HEADER_PATTERNS = [
  /total effort/, // Total Effort (Hrs)
  /per hour cost/, // Employee Per Hour Cost
  /employee total cost/, // Employee Total Cost
];

const NUMERIC_CELL_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatNumericCellValue = (value) => {
  if (value === undefined || value === null || value === '') return value;
  const numericValue = typeof value === 'number'
    ? value
    : Number(
      String(value)
        .replace(/,/g, '')
        .trim(),
    );
  if (!Number.isFinite(numericValue)) return value;
  return NUMERIC_CELL_FORMATTER.format(numericValue);
};

const applyProjectCostNumericFormatting = (tableConfiguration, tableData) => {
  const numericFields = (tableConfiguration || [])
    .filter((col) => {
      const headerText = (col.header || '').toLowerCase();
      return NUMERIC_COLUMN_HEADER_PATTERNS.some((pattern) => pattern.test(headerText));
    })
    .map((col) => col.field);

  if (!numericFields.length) return tableData;

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const updatedRow = { ...row };
    numericFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedRow, field)) {
        updatedRow[field] = formatNumericCellValue(updatedRow[field]);
      }
    });
    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return tableData.map(formatRow);
};

const handleCellClick = (arg1, arg2) => {
  const fieldPath = arg1?.fieldPath || arg2?.field || arg2?.columnKey || arg1?.field;
  const rowData = arg1?.rowData || arg1;
  const isProjectNameCol = fieldPath === 'projectName' || fieldPath === 'project_name';
  const idValue = rowData?.projectId || rowData?.project_seq_id || rowData?.project_id;
  if (isProjectNameCol && idValue) {
    const targetUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(idValue))}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

const applyProjectNameLink = (data) => {
  if (!Array.isArray(data)) return data;

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const existingConfig = row.rowConfiguration || {};
    const existingOnClick = Array.isArray(existingConfig.onClick) ? existingConfig.onClick : [];
    const fieldsToAdd = ['projectName', 'project_name'].filter(
      (field) => !existingOnClick.some((item) => item?.field === field),
    );

    const updatedRow = {
      ...row,
      ...(fieldsToAdd.length > 0
        ? {
          rowConfiguration: {
            ...existingConfig,
            onClick: [
              ...existingOnClick,
              ...fieldsToAdd.map((field) => ({ field })),
            ],
          },
        }
        : {}),
    };

    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return data.map(formatRow);
};

const handleFilteredApiResponse = (response, tableConfigurationCacheRef, tableConfigInjectedRef) => {
  if (!response) return;

  let rawTableConfiguration = [];
  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  }

  const normalizedTableConfiguration = rawTableConfiguration.map((column) => ({
    ...column,
    menuItems: normalizeMenuItems(Array.isArray(column?.menuItems) ? column.menuItems : []),
  }));

  if (normalizedTableConfiguration.length > 0) {
    const projectNameCol = normalizedTableConfiguration.find((col) => col.field === 'projectName');
    const projectIdCol = normalizedTableConfiguration.find((col) => col.field === 'projectId');
    const remainingCols = normalizedTableConfiguration.filter(
      (col) => col.field !== 'projectName' && col.field !== 'projectId',
    );

    rawTableConfiguration = [
      ...(projectNameCol
        ? [{ ...projectNameCol, pinned: true, defaultPinned: true, width: 200, headerAlign: 'left' }]
        : []),
      ...(projectIdCol ? [{ ...projectIdCol, pinned: true, defaultPinned: true, width: 100 }] : []),
      ...remainingCols,
    ];
    if (tableConfigurationCacheRef) tableConfigurationCacheRef.current = rawTableConfiguration;
  } else if (tableConfigurationCacheRef?.current?.length) {
    rawTableConfiguration = tableConfigurationCacheRef.current;
  } else {
    rawTableConfiguration = normalizedTableConfiguration;
  }

  const shouldInjectConfig = tableConfigInjectedRef
    ? !tableConfigInjectedRef.current && rawTableConfiguration.length > 0
    : true;

  if (shouldInjectConfig && tableConfigInjectedRef) {
    tableConfigInjectedRef.current = true;
  }

  const rawTableData = response.data?.tableData || response.data?.data?.tableData || [];
  const formattedTableData = applyProjectCostNumericFormatting(rawTableConfiguration, rawTableData);
  const dataWithProjectLink = applyProjectNameLink(formattedTableData);

  response.data = {
    ...response.data,
    ...(shouldInjectConfig ? { tableConfiguration: rawTableConfiguration } : {}),
    tableData: dataWithProjectLink,
    data: {
      ...(response.data?.data || {}),
      ...(shouldInjectConfig ? { tableConfiguration: rawTableConfiguration } : {}),
      tableData: dataWithProjectLink,
    },
  };

  const backendMeta = response.data?.meta || response.data?.data?.meta || {};
  const totalRecords = Number(backendMeta.total ?? response.meta?.total ?? 0);

  response.meta = {
    ...(response.meta || {}),
    total: totalRecords,
    page: Number(backendMeta.page ?? response.meta?.page ?? 1),
    limit: Number(backendMeta.limit ?? response.meta?.limit ?? 10),
  };
};

const ToolBarHeading = () => (
  <div className="project-employee-cost-toolbar-heading">
    <span className="project-employee-cost-toolbar-heading-title">
      Project Employee Cost Report
    </span>
    <div className="project-employee-cost-toolbar-heading-right">
      <span className="project-employee-cost-toolbar-heading-note">
        <strong>Note:</strong> The amounts in the table are in INR format
      </span>
      <span className="project-employee-cost-toolbar-heading-separator" aria-hidden="true" />
    </div>
  </div>
);

const getMonthSpan = (fromStr, toStr) => {
  if (!fromStr || !toStr) return 0;
  const fromMatch = fromStr.match(/^(\d{4})-(\d{2})-\d{2}$/);
  const toMatch = toStr.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!fromMatch || !toMatch) return 0;

  const fromYear = parseInt(fromMatch[1], 10);
  const fromMonth = parseInt(fromMatch[2], 10);
  const toYear = parseInt(toMatch[1], 10);
  const toMonth = parseInt(toMatch[2], 10);

  return (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;
};

const useProjectEmployeeCostTableEffects = ({
  containerRef,
  resetToCurrentDate,
  setSelectedMonthRange,
  setTableScrollHeight,
  onApplyFilters,
  toastRef,
}) => {
  useEffect(() => {
    document.body.classList.add('project-employee-cost-page');
    return () => {
      document.body.classList.remove('project-employee-cost-page');
    };
  }, []);

  useEffect(() => {
    const rootNode = containerRef.current;

    const handleResetClick = (event) => {
      if (!rootNode) return;
      const resetButton = event.target.closest('button');
      if (!resetButton || !rootNode.contains(resetButton)) return;
      const clickedResetIcon = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(resetButton.querySelector('.pi-refresh'));
      if (clickedResetIcon) resetToCurrentDate();
    };

    rootNode?.addEventListener('click', handleResetClick);

    const handleResize = () => {
      setTableScrollHeight('auto');
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetClick);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, resetToCurrentDate, setTableScrollHeight]);

  useEffect(() => {
    let timer = null;
    const observer = new MutationObserver(() => {
      if (timer) cancelAnimationFrame(timer);
      timer = requestAnimationFrame(() => {
        const menuEls = document.querySelectorAll(
          '.responsive-tiered-menu1, .p-tieredmenu, .tu-filter-container',
        );
        menuEls.forEach((menu) => {
          const textNodes = menu.querySelectorAll('.p-menuitem-text, .p-tieredmenu-label, span');
          textNodes.forEach((node) => {
            const txt = (node.textContent || '').trim();
            if ((txt === 'Project Name' || txt === 'Project Name *')
              && !node.querySelector('.project-name-required-asterisk')) {
              if (txt === 'Project Name *') {
                node.childNodes.forEach((child) => {
                  if (child.nodeType === Node.TEXT_NODE && child.textContent.includes('*')) {
                    child.textContent = child.textContent.replace('*', '').trim();
                  }
                });
              }
              const starSpan = document.createElement('span');
              starSpan.className = 'project-name-required-asterisk';
              starSpan.textContent = ' *';
              node.appendChild(starSpan);
            }
          });
        });

        const container = document.querySelector('.calendar-filter-container');
        if (container && container.dataset.autoOpened !== 'true') {
          const input = container.querySelector('input');
          if (!input) return;

          container.dataset.autoOpened = 'true';
          setTimeout(() => {
            input.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
            input.focus();
            input.click();
          }, 0);
          return;
        }

        const menu = document.querySelector('.responsive-tiered-menu1, .p-tieredmenu');
        if (!menu || menu.dataset.autoOpened === 'true') return;

        const monthInput = menu.querySelector('.p-calendar input');
        if (!monthInput) return;

        menu.dataset.autoOpened = 'true';
        setTimeout(() => {
          monthInput.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          monthInput.focus();
          monthInput.click();
        }, 0);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (timer) cancelAnimationFrame(timer);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const clearSelectedMonthRange = () => {
      setSelectedMonthRange({ fromMonthYear: null, toMonthYear: null });
    };

    const syncSelectedMonthRange = (container) => {
      const nextRange = extractMonthRangePayload(container);
      setSelectedMonthRange(nextRange);
      return nextRange;
    };

    const handleFilterFooterClick = (event) => {
      // 1. Main filter footer Apply — sync range then let table utility's own
      //    handler fire naturally (do NOT stop propagation).
      const mainApplyBtn = event.target.closest('.tu-filter-footer-apply');
      if (mainApplyBtn) {
        const calendarContainer = document.querySelector('.month-wise-calendar-filter-container');
        if (calendarContainer) {
          const nextRange = extractMonthRangePayload(calendarContainer);
          if (nextRange && nextRange.fromMonthYear && nextRange.toMonthYear) {
            const span = getMonthSpan(nextRange.fromMonthYear, nextRange.toMonthYear);
            if (span > 3) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
          }
          syncSelectedMonthRange(calendarContainer);
        }
        if (onApplyFilters) onApplyFilters(true);
        return;
      }

      // 2. Main filter footer Reset / toolbar Reset / Clear
      const footerReset = event.target.closest('.tu-filter-footer-reset');
      if (footerReset) {
        clearSelectedMonthRange();
        if (onApplyFilters) onApplyFilters(false);
        return;
      }

      const targetBtn = event.target.closest('button') || event.target.closest('.p-button');
      if (targetBtn) {
        const labelEl = targetBtn.querySelector('.p-button-label');
        const label = (labelEl ? labelEl.textContent : targetBtn.textContent || '').trim();
        if (label === 'Reset All' || label === 'Clear') {
          clearSelectedMonthRange();
          if (onApplyFilters) onApplyFilters(false);
          return;
        }
      }

      // 3. Month & Year sub-popup buttons (inside .month-wise-calendar-filter-container)
      const calendarButton = event.target.closest('.month-wise-calendar-filter-container .p-button');
      if (!calendarButton) return;

      const monthContainer = calendarButton.closest('.month-wise-calendar-filter-container');
      const labelEl = calendarButton.querySelector('.p-button-label');
      const buttonLabel = labelEl?.textContent?.trim();

      if (buttonLabel === 'Reset') {
        clearSelectedMonthRange();
        const errorEl = monthContainer.querySelector('.month-range-validation-error');
        if (errorEl) errorEl.remove();
        if (onApplyFilters) onApplyFilters(false);
        return;
      }

      if (buttonLabel !== 'Apply') return;

      const nextRange = extractMonthRangePayload(monthContainer);
      if (nextRange && nextRange.fromMonthYear && nextRange.toMonthYear) {
        const span = getMonthSpan(nextRange.fromMonthYear, nextRange.toMonthYear);
        if (span > 3) {
          let errorEl = monthContainer.querySelector('.month-range-validation-error');
          if (!errorEl) {
            errorEl = document.createElement('div');
            errorEl.className = 'month-range-validation-error';
            errorEl.style.color = 'red';
            errorEl.style.fontSize = '12px';
            errorEl.style.marginTop = '8px';
            errorEl.style.fontFamily = 'Mukta-Regular, sans-serif';
            errorEl.style.textAlign = 'center';
            errorEl.style.width = '100%';
            errorEl.style.display = 'block';
            errorEl.style.gridColumn = '1 / -1';
            errorEl.textContent = 'The selected date range cannot exceed 3 months';
            monthContainer.appendChild(errorEl);
          }

          event.preventDefault();
          event.stopPropagation();
          return;
        } else {
          const errorEl = monthContainer.querySelector('.month-range-validation-error');
          if (errorEl) errorEl.remove();
        }
      }

      // Sync the ref (for API payload) — do NOT stop propagation so the table
      // utility's own handleApplyFilter also fires and stores selectedValue in
      // its draft state. This mirrors Revenue Recognition's behaviour exactly.
      syncSelectedMonthRange(monthContainer);
    };

    document.addEventListener('click', handleFilterFooterClick, true);
    return () => document.removeEventListener('click', handleFilterFooterClick, true);
  }, [setSelectedMonthRange, onApplyFilters, toastRef]);


};

const ProjectEmployeeCost = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const selectedMonthRangeRef = useRef({ fromMonthYear: null, toMonthYear: null });
  const userAppliedFiltersRef = useRef(false);

  const [tableScrollHeight, setTableScrollHeight] = useState('auto');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [, setLoading] = useState(false);
  const toastRef = useRef(null);
  const tableConfigurationCacheRef = useRef([]);
  const tableConfigInjected = useRef(false);
  // Reference to receive TableUtility's state handlers (setTableData, setPagination, etc.)
  const tableStateHandlerRef = useRef(null);

  const setSelectedMonthRange = useCallback((nextRange) => {
    selectedMonthRangeRef.current = {
      fromMonthYear: nextRange?.fromMonthYear || null,
      toMonthYear: nextRange?.toMonthYear || null,
    };
  }, []);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    setLoading(false);
    const tableData = response?.data?.tableData || response?.data?.data?.tableData || [];
    if (tableData.length === 0) {
      userAppliedFiltersRef.current = false;
      setHasAppliedFilters(false);
    }

    handleFilteredApiResponse(response, tableConfigurationCacheRef, tableConfigInjected);
  }, []);

  const resetToCurrentDate = useCallback(() => {
    userAppliedFiltersRef.current = false;
    tableConfigInjected.current = false;
    setSelectedMonthRange({ fromMonthYear: null, toMonthYear: null });
    setHasAppliedFilters(false);
    setLoading(false);
  }, []);

  const handleApplyFiltersState = useCallback((applied) => {
    const isApplied = Boolean(applied);
    userAppliedFiltersRef.current = isApplied;
    setHasAppliedFilters(isApplied);
    if (isApplied) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, []);

  useProjectEmployeeCostTableEffects({
    containerRef,
    resetToCurrentDate,
    setSelectedMonthRange,
    setTableScrollHeight,
    onApplyFilters: handleApplyFiltersState,
    toastRef,
  });

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Project Employee Cost'),
          sheetName: () => getExportFileNameWithDate('Project Employee Cost'),
        },
      },

      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
        setTableStateHandler: (getTableState) => {
          tableStateHandlerRef.current = getTableState();
        },
      },

      additionalPayloadData: {
        get fromMonthYear() {
          return selectedMonthRangeRef.current.fromMonthYear;
        },
        get toMonthYear() {
          return selectedMonthRangeRef.current.toMonthYear;
        },
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },

    tableSettings: {
      size: 'small',
      loaderSkeleton: { rows: 8, bodyCells: 2, columns: 7 },
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
      paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [10, 20, 50, 100] },
    },
  }), [tableScrollHeight, handleFilteredApiResponseWithState, userProfile]);

  const isInitialState = !hasAppliedFilters;
  const containerClassName = [
    'project-employee-cost-outer-layer',
    isInitialState ? 'project-employee-cost--initial' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={containerClassName}
    >
      <Toast ref={toastRef} />
      <div className="project-employee-cost">
        <div className="project-employee-cost-table-utility">
          <TableUtility configuration={configuration} />

        </div>
        {isInitialState && <MilestoneInitial />}
      </div>
    </div>
  );
};

export default ProjectEmployeeCost;
